<template>
  <div
    class="product-block"
    :class="[variant, { 'is-main': isMainProduct, 'preview-mode': isPreview, 'empty-product': !safeProduct || !safeProduct.id }]"
    :data-product-id="safeProduct?.id"
  >
    <!-- Bouton de suppression -->
    <button
      v-if="!isPreview && safeProduct && safeProduct.id"
      class="remove-button"
      @click="
        () => {
          resetProductData();
          $emit('remove');
        }
      "
    >
      <span class="text-red-600">×</span>
    </button>

    <div class="product-content" :class="{ 'image-right': isImageRight }">
      <!-- Zone de l'image à gauche -->
      <div
        class="image-section"
        @dragover.prevent
        @drop.prevent="onProductDrop"
      >
        <div class="image-container" :style="{ background: backgroundColor }">
          <img
            v-if="safeProduct?.image || safeProduct?.image_path || safeProduct?.image_name"
            :src="
              safeProduct?.image || getProductImageUrl(safeProduct?.image_path || safeProduct?.image_name)
            "
            :alt="safeProduct?.name"
            :class="[
              'product-image',
              { 'remove-background': removeBackground },
            ]"
            @error="handleImageError"
          >
          <template v-else>
            <div v-if="!isPreview" class="empty-state">
              Déposez un produit ici
            </div>
            <div v-else class="empty-state-preview">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 100 100"
                class="default-product-svg"
                aria-hidden="true"
                focusable="false"
              >
                <rect width="100" height="100" fill="#f3f4f6" />
                <path
                  d="M85 20H15C12.24 20 10 22.24 10 25V75C10 77.76 12.24 80 15 80H85C87.76 80 90 77.76 90 75V25C90 22.24 87.76 20 85 20ZM80 70H20V30H80V70Z"
                  fill="#d1d5db"
                />
                <circle
                  cx="35"
                  cy="45"
                  r="5"
                  fill="#d1d5db"
                />
                <path d="M70 55L50 75L40 65L20 85H80L70 55Z" fill="#e5e7eb" />
              </svg>
            </div>
          </template>

          <!-- Contrôles de l'image -->
          <button
            v-if="safeProduct?.image && !isPreview"
            class="background-toggle"
            :title="
              removeBackground ? 'Restaurer le fond' : 'Supprimer le fond'
            "
            @click="toggleBackground"
          >
            <span class="icon">{{ removeBackground ? "🎨" : "✨" }}</span>
          </button>
          <button
            v-if="!isPreview && safeProduct && safeProduct.id"
            class="background-color-toggle"
            :title="'Changer la couleur du fond'"
            @click="toggleColorPicker"
          >
            <span class="icon">🎨</span>
          </button>
          <!-- Color picker -->
          <div v-if="showColorPicker" class="color-picker" @click.stop>
            <div class="color-options">
              <button
                v-for="color in predefinedColors"
                :key="color.value"
                class="color-option"
                :style="{ background: color.value }"
                :title="color.name"
                @click="selectBackgroundColor(color.value)"
              />
            </div>
            <input
              v-model="customColor"
              type="color"
              class="custom-color-input"
              @change="selectBackgroundColor(customColor)"
            >
          </div>

          <!-- Pictogram zone -->
          <div
            v-if="safeProduct && safeProduct.id"
            class="picto-zone"
            @dragover.prevent
            @drop.prevent="onPictoDrop"
          >
            <img
              v-if="pictogram"
              :src="getPictoImageUrl(pictogram.image_name)"
              :alt="pictogram.name"
              class="picto-image"
              :style="{ transform: `scale(${pictoScale})` }"
            >
            <!-- Contrôles de taille -->
            <div v-if="pictogram" class="picto-controls">
              <button class="size-control" @click="adjustPictoSize(-0.1)">
                -
              </button>
              <button class="size-control" @click="adjustPictoSize(0.1)">
                +
              </button>
              <button class="remove-picto" @click="removePicto">
                <span class="text-red-600">×</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Zone des informations à droite -->
      <div v-if="safeProduct && safeProduct.id" class="info-section">
        <div class="product-info">
          <div class="title-container">
            <div
              v-if="!isPreview || (safeProduct?.name && safeProduct.name.trim())"
              class="product-title"
              :style="{ transform: `scale(${titleScale})`, transformOrigin: 'left' }"
              :contenteditable="!isPreview"
              @blur="updateTitle"
              v-text="safeProduct?.name ? safeProduct.name.toUpperCase() : ''"
            />
            <!-- Contrôles de taille du titre -->
            <div v-if="!isPreview" class="title-controls">
              <button class="size-control" @click="adjustTitleSize(-0.1)">
                -
              </button>
              <button class="size-control" @click="adjustTitleSize(0.1)">
                +
              </button>
            </div>
          </div>

          <div
            v-if="
              !isPreview ||
                (safeProduct?.description && safeProduct.description.trim())
            "
            class="product-description"
            :contenteditable="!isPreview"
            @blur="updateDescription"
            @keydown.enter.prevent="handleEnterKey"
            v-text="safeProduct?.description || ''"
          />

          <div class="price-section">
            <div class="price-actions">
              <div v-if="!isPreview && showPrice" class="price-controls">
                <button class="size-control" @click="adjustPriceSize(-0.1)">
                  -
                </button>
                <button class="size-control" @click="adjustPriceSize(0.1)">
                  +
                </button>
              </div>
              <button
                v-if="!isPreview"
                class="toggle-price-button"
                :title="showPrice ? 'Masquer le prix' : 'Afficher le prix'"
                @click="showPrice = !showPrice"
              >
                <span class="icon">{{ showPrice ? "👁️" : "🚫" }}</span>
              </button>
            </div>
            <div v-if="showPrice" class="product-price flex-grow">
              <div class="price-container">
                <div class="price-ht-container">
                  <span
                    class="price-ht"
                    :class="priceSize"
                    :contenteditable="true"
                    @blur="updatePrice"
                    @keydown.enter.prevent="handlePriceEnter"
                    v-text="formatPrice(product?.price || 0)"
                  />
                  <span class="price-suffix ht" :class="priceSize">€HT</span>
                </div>
              </div>
              <div class="price-ttc-container">
                <span class="price-ttc" :class="priceTTCSize">
                  {{ formatPriceTTC(product?.price || 0) }}
                  <span class="ttc-text">€ TTC</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Brand zone -->
          <div
            class="brand-container"
            :class="{ 'align-left': position % 2 === 1 }"
          >
            <div
              class="brand-zone-info"
              @dragover.prevent
              @drop.prevent="onBrandDrop"
              @dragenter.prevent
              @dragleave.prevent
            >
              <img
                v-if="brand"
                :src="getBrandImageUrl(brand.image_name)"
                :alt="brand.name"
                class="brand-image"
                :style="{ transform: `scale(${brandScale})` }"
              >
              <div v-if="brand && !isPreview" class="brand-controls">
                <button class="size-control" @click="adjustBrandSize(-0.1)">
                  -
                </button>
                <button class="size-control" @click="adjustBrandSize(0.1)">
                  +
                </button>
                <button class="remove-brand" @click="removeBrand">
                  <span class="text-red-600">×</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useProductCustomizationStore } from "@/stores/supports/useProductCustomizationStore";
import { computed, onMounted, ref } from "@vue/runtime-core";

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  image: string;
  reference?: string;
  image_name?: string;
  base_price?: string;
  category_id?: number;
}

const props = defineProps<{
  product: Product | null;
  variant: "columnOne" | "rowOne" | "rowTwo" | "columnTwo";
  isMainProduct?: boolean;
  isPreview?: boolean;
  fontFamily: string;
  fontSize: string;
  priceSize?: "price-sm" | "price-md" | "price-lg";
  priceTTCSize?: "price-ttc-sm" | "price-ttc-md" | "price-ttc-lg";
  position: number;
}>();

// Vérification de sécurité pour éviter les erreurs de rendu
const safeProduct = computed(() => {
  try {
    if (!props.product || !props.product.id) {
      return null;
    }
    return {
      ...props.product,
      id: Number(props.product.id) || 0,
      name: props.product.name || "",
      description: props.product.description || "",
      price: props.product.price || 0,
      image: props.product.image || "",
      reference: props.product.reference || "",
      image_name: props.product.image_name || "",
      image_path: props.product.image_path || "",
    };
  } catch (error) {
    console.error("Erreur dans safeProduct computed:", error);
    return null;
  }
});

const emit = defineEmits<{
  (e: "remove"): void;
  (e: "update:product", product: Product): void;
}>();

const customizationStore = useProductCustomizationStore();

// État local pour chaque instance
const showColorPicker = ref(false);
const customColor = ref("#f8f8f8");
const showPrice = ref(true);

// Récupérer les données de personnalisation du store
const customization = computed(() => {
  try {
    if (!safeProduct.value?.id)
      return customizationStore.getDefaultCustomization();
    return customizationStore.getCustomization(safeProduct.value.id);
  } catch (error) {
    console.error("Erreur dans customization computed:", error);
    return customizationStore.getDefaultCustomization();
  }
});

// Utiliser les données du store
const removeBackground = computed({
  get: () => customization.value.removeBackground || false,
  set: (value) => {
    if (props.product?.id) {
      customizationStore.setCustomization(props.product.id, {
        removeBackground: value,
      });
    }
  },
});

const backgroundColor = computed({
  get: () => {
    if (!safeProduct.value) return "#f8f8f8";
    return customization.value.backgroundColor || "#fff";
  },
  set: (value) => {
    if (safeProduct.value?.id) {
      customizationStore.setCustomization(safeProduct.value.id, {
        backgroundColor: value,
      });
    }
  },
});

const pictogram = computed({
  get: () => customization.value.pictogram || null,
  set: (value) => {
    if (safeProduct.value?.id) {
      customizationStore.setCustomization(safeProduct.value.id, {
        pictogram: value,
      });
    }
  },
});

const pictoScale = computed({
  get: () => customization.value.pictoScale || 1,
  set: (value) => {
    if (safeProduct.value?.id) {
      customizationStore.setCustomization(safeProduct.value.id, {
        pictoScale: value,
      });
    }
  },
});

const priceScale = computed({
  get: () => customization.value.priceScale || 1,
  set: (value) => {
    if (safeProduct.value?.id) {
      customizationStore.setCustomization(safeProduct.value.id, {
        priceScale: value,
      });
    }
  },
});

const titleScale = computed({
  get: () => customization.value.titleScale || 1,
  set: (value) => {
    if (safeProduct.value?.id) {
      customizationStore.setCustomization(safeProduct.value.id, {
        titleScale: value,
      });
    }
  },
});

const brand = computed({
  get: () => customization.value.brand || null,
  set: (value) => {
    if (safeProduct.value?.id) {
      customizationStore.setCustomization(safeProduct.value.id, {
        brand: value,
      });
    }
  },
});

const brandScale = computed({
  get: () => {
    const custom = safeProduct.value?.id
      ? customizationStore.getCustomization(safeProduct.value.id)
      : null;
    return custom && typeof custom.brandScale === "number"
      ? custom.brandScale
      : 1;
  },
  set: (value) => {
    if (safeProduct.value?.id) {
      customizationStore.setCustomization(safeProduct.value.id, {
        brandScale: value,
      });
    }
  },
});

const predefinedColors = [
  { name: "Gris clair", value: "#f8f8f8" },
  { name: "Blanc", value: "#ffffff" },
  { name: "Noir", value: "#000000" },
  { name: "Rouge AGCO", value: "#B70018" },
  { name: "Gris foncé", value: "#333333" },
  { name: "Transparent", value: "transparent" },
];

// Supprimer l'initialisation des valeurs locales
onMounted(() => {
  if (props.product) {
  }
});

const updateTitle = (event: Event) => {
  const target = event.target as HTMLElement;
  if (safeProduct.value) {
    emit("update:product", {
      ...safeProduct.value,
      name: target.innerText,
    });
  }
};

const updateDescription = (event: Event) => {
  const target = event.target as HTMLElement;
  if (safeProduct.value) {
    emit("update:product", {
      ...safeProduct.value,
      description: target.innerText,
    });
  }
};

const handleEnterKey = () => {
  document.execCommand("insertLineBreak");
};

const handlePriceEnter = (event: KeyboardEvent) => {
  const target = event.target as HTMLElement;
  target.blur();
};

const updatePrice = (event: Event) => {
  const target = event.target as HTMLElement;
  if (safeProduct.value) {
    const priceText = target.innerText.replace(/[^0-9.,]/g, "");
    const price = parseFloat(priceText.replace(",", "."));

    if (!isNaN(price)) {
      const newPrice = Math.min(price, 999999.99);
      emit("update:product", {
        ...safeProduct.value,
        price: newPrice,
      });
      // Mettre à jour l'affichage avec le format correct
      target.innerText = formatPrice(newPrice);
    } else {
      // Restaurer le prix précédent si la valeur n'est pas valide
      target.innerText = formatPrice(safeProduct.value.price);
    }
  }
};

const formatPrice = (price: number | null) => {
  if (price === null) return "0,00";
  return price.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPriceTTC = (price: number | null) => {
  if (price === null) return "0,00";
  const ttc = price * 1.2; // TVA 20%
  return ttc.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// URL helpers
const apiUrl = import.meta.env.VITE_API_URL;

const getBrandImageUrl = (imageName: string) => {
  return `${apiUrl}/upl/brands/${imageName}`;
};

const getProductImageUrl = (imageNameOrPath: string) => {
  if (imageNameOrPath.startsWith('products/')) {
    return `${apiUrl}/upl/${imageNameOrPath}`;
  }
  return `${apiUrl}/upl/products/${imageNameOrPath}`;
};

const handleImageError = (event: Event) => {
  console.error("Erreur de chargement de l'image:", event);
  const img = event.target as HTMLImageElement;
  img.style.display = "none";
};

const getPictoImageUrl = (imageName: string) => {
  return `${apiUrl}/upl/pictos/${imageName}`;
};

// Drop handlers
const onBrandDrop = (event: DragEvent) => {
  try {
    const data = JSON.parse(event.dataTransfer?.getData("text/plain") || "{}");
    if (data.type === "brand" && data.data) {
      brand.value = {
        id: data.data.id,
        name: data.data.name,
        image_name: data.data.image_name,
      };
    }
  } catch (error) {
    console.error("Erreur lors du drop de la marque:", error);
  }
};

const onPictoDrop = (event: DragEvent) => {
  try {
    const data = JSON.parse(event.dataTransfer?.getData("text/plain") || "{}");
    if (data.type === "pictogram" && data.data) {
      pictogram.value = {
        id: data.data.id,
        name: data.data.name,
        image_name: data.data.image_name,
      };
    }
  } catch (error) {
    console.error("Erreur lors du drop du pictogramme:", error);
  }
};

const onProductDrop = (event: DragEvent) => {
  try {
    const data = JSON.parse(event.dataTransfer?.getData("text/plain") || "{}");
    if (data.type === "product" && data.data) {
      emit("update:product", {
        ...data.data,
        product_image_id: data.data.product_image_id,
      });
    }
  } catch (error) {
    console.error("Erreur lors du drop du produit:", error);
  }
};

const toggleBackground = () => {
  removeBackground.value = !removeBackground.value;
};

const adjustPictoSize = (delta: number) => {
  const newScale = pictoScale.value + delta;
  pictoScale.value = newScale;
};

const toggleColorPicker = () => {
  showColorPicker.value = !showColorPicker.value;
};

const selectBackgroundColor = (color: string) => {
  backgroundColor.value = color;
  customColor.value = color;
  showColorPicker.value = false;
};

const removePicto = () => {
  pictogram.value = null;
};

const resetProductData = () => {
  brand.value = null;
  pictogram.value = null;
  removeBackground.value = false;
  pictoScale.value = 1;
  showColorPicker.value = false;
  backgroundColor.value = "#f8f8f8";
  customColor.value = "#f8f8f8";
};

// Fermer le color picker quand on clique ailleurs
onMounted(() => {
  document.addEventListener("click", (event) => {
    if (showColorPicker.value) {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".color-picker") &&
        !target.closest(".background-color-toggle")
      ) {
        showColorPicker.value = false;
      }
    }
  });
});

// Computed pour déterminer si l'image doit être à droite
const isImageRight = computed(() => {
  return props.position % 2 === 0;
});

const adjustPriceSize = (delta: number) => {
  const newScale = priceScale.value + delta;
  priceScale.value = newScale;
  // Émettre le changement vers le parent
  if (safeProduct.value) {
    emit("update:product", {
      ...safeProduct.value,
      priceScale: newScale,
    } as any);
  }
};

const adjustTitleSize = (delta: number) => {
  const newScale = titleScale.value + delta;
  titleScale.value = newScale;
  // Émettre le changement vers le parent
  if (safeProduct.value) {
    emit("update:product", {
      ...safeProduct.value,
      titleScale: newScale,
    } as any);
  }
};

const adjustBrandSize = (delta: number) => {
  const newScale = brandScale.value + delta;
  brandScale.value = newScale;
  // Émettre le changement vers le parent
  if (safeProduct.value) {
    emit("update:product", {
      ...safeProduct.value,
      brandScale: newScale,
    } as any);
  }
};

const removeBrand = () => {
  brand.value = null;
};
</script>

<script lang="ts">
export default {
  name: "ProductBlockEmail",
};
</script>

<style scoped>
.product-block {
  position: relative;
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 0.5rem;
  overflow: visible;
  display: flex;
  flex-direction: column;
  border: none;
}

.product-content {
  display: flex;
  flex-direction: row;
  padding: 0.75rem;
  gap: 1rem;
  width: 100%;
}

.product-content.image-right {
  flex-direction: row-reverse;
}

.image-section {
  flex: 0 0 50%;
  min-width: 200px;
  max-width: 350px;
  min-height: 250px;
  display: flex;
  align-items: center;
  flex-shrink: 1;
}

.info-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
}

.image-container {
  width: 100%;
  /* height: 100%; */
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0;
  background: #f8f8f8;
  border-radius: 0.5rem;
  border: none;
  min-height: 250px;
  flex-shrink: 1;
  isolation: isolate;
}

.product-image {
  width: 100% !important;
  /* height: 100% !important; */
  object-fit: contain !important;
  background: transparent;
  transition: mix-blend-mode 0.3s ease;
  max-width: 100%;
  max-height: 100%;
  display: block;
  margin: 0 auto;
}

.product-image.remove-background {
  mix-blend-mode: multiply;
  position: relative;
  z-index: 2;
  isolation: isolate;
}

.empty-state {
  color: #999;
  font-size: 0.875rem;
}

/* Styles pour l'état vide en mode aperçu */
.empty-state-preview {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f8f8; /* Correspond au fond image-container */
}

.default-product-svg {
  width: 60%; /* Ajustez la taille si besoin */
  height: 60%; /* Ajustez la taille si besoin */
  max-width: 100px;
  max-height: 100px;
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.product-title {
  font-weight: 600;
  font-size: 1.8rem !important;
  line-height: 1.4;
  margin-bottom: 0.5rem;
}

.title-container {
  position: relative;
  display: inline-block;
}

.title-controls {
  position: absolute;
  top: -2.5rem;
  right: 0;
  display: flex;
  gap: 0.25rem;
  background: white;
  padding: 0.25rem;
  border-radius: 0.25rem;
  border: 1px solid #ccc;
  z-index: 50;
}

.product-description {
  font-size: 1.4rem !important;
  color: #666;
  line-height: 1.6;
  margin-bottom: 0.75rem;
  white-space: pre-wrap;
  position: relative;
}

.product-description:focus {
  z-index: 60;
}

.price-section {
  margin-top: 0.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex-direction: row-reverse;
  justify-content: space-between;
}

.price-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.product-price {
  flex-grow: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.price-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: flex-start;
}

.price-ht-container {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  transform-origin: left;
  transform: scale(v-bind(priceScale));
}

.price-ht {
  font-size: 1.6rem;
  font-weight: 600;
  color: rgb(var(--price-color));
  display: inline-block;
}

.price-suffix.ht {
  font-size: 1.4rem;
  color: rgb(var(--price-color));
  font-weight: 600;
  white-space: nowrap;
  display: inline-block;
}

.price-controls {
  display: inline-flex;
  gap: 0.25rem;
  align-items: center;
}

.price-ttc-container {
  margin-top: 0.02rem;
  transform-origin: left;
  transform: scale(v-bind(priceScale));
}

.price-ttc {
  color: #666;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
}

.ttc-text {
  margin-left: 0.25rem;
}

/* Tailles de prix */
.price-sm {
  font-size: 1.4rem; /* Augmenter de 1.2rem à 1.4rem */
}

.price-md {
  font-size: 1.8rem; /* Augmenter de 1.6rem à 1.8rem */
}

.price-lg {
  font-size: 2.2rem; /* Augmenter de 2rem à 2.2rem */
}

/* Tailles de prix TTC */
.price-ttc-sm {
  font-size: 0.8rem; /* Augmenter de 0.6rem à 0.8rem */
}

.price-ttc-md {
  font-size: 1rem; /* Augmenter de 0.8rem à 1rem */
}

.price-ttc-lg {
  font-size: 1.2rem; /* Augmenter de 1rem à 1.2rem */
}

/* Variants */
.columnOne .product-content,
.columnTwo .product-content {
  padding: 0.5rem;
}

.columnOne .image-container,
.columnTwo .image-container {
  flex: 1;
  min-height: 0;
  min-height: 200px;
  padding: 0;
}

.columnOne .product-info,
.columnTwo .product-info {
  flex: none;
}

.rowOne .product-content,
.rowTwo .product-content {
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem;
}

.rowOne .image-container,
.rowTwo .image-container {
  flex: 1;
  max-width: 50%;
  height: 100%;
  padding: 0;
}

.rowOne .product-info,
.rowTwo .product-info {
  flex: 1;
  justify-content: center;
  padding-right: 0.5rem;
}

.rowOne.is-main .product-content,
.rowTwo.is-main .product-content {
  padding: 0.75rem !important;
}

.rowOne.is-main .image-container,
.rowTwo.is-main .image-container {
  max-width: 100%;
  padding: 0 !important;
}

.rowOne.is-main .product-info,
.rowTwo.is-main .product-info {
  flex: 1;
  padding-right: 2rem;
}

.is-main .product-title {
  font-size: 1.6rem;
  margin-bottom: 0.75rem;
}

.is-main .product-description {
  font-size: 1.4rem;
  max-height: none !important;
  -webkit-line-clamp: 3;
  margin-bottom: 1.25rem;
}

.is-main .price-ht {
  font-size: 1.6rem;
}

.is-main .price-ttc {
  font-size: 0.8rem;
}

/* Styles spécifiques pour les layouts avec 4, 5 et 6 produits */
.products-container[class*="grid-4"] .is-main .product-title,
.products-container[class*="grid-5"] .is-main .product-title,
.products-container[class*="grid-6"] .is-main .product-title {
  font-size: 1.6rem;
  margin-bottom: 0.5rem;
}

.products-container[class*="grid-4"] .is-main .product-description,
.products-container[class*="grid-5"] .is-main .product-description,
.products-container[class*="grid-6"] .is-main .product-description {
  font-size: 1.2rem;
  margin-bottom: 1rem;
}

.products-container[class*="grid-4"] .is-main .price-ht,
.products-container[class*="grid-5"] .is-main .price-ht,
.products-container[class*="grid-6"] .is-main .price-ht {
  font-size: 1.4rem;
}

.products-container[class*="grid-4"] .is-main .price-ttc,
.products-container[class*="grid-5"] .is-main .price-ttc,
.products-container[class*="grid-6"] .is-main .price-ttc {
  font-size: 0.7rem;
}

.products-container[class*="grid-4"] .is-main .image-container,
.products-container[class*="grid-5"] .is-main .image-container,
.products-container[class*="grid-6"] .is-main .image-container {
  max-height: 50%;
  min-height: 200px;
}

.remove-button {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: rgb(var(--price-color));
  z-index: 10;
}

.picto-zone {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 8rem;
  height: 3rem;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  background: transparent;
  border: 1px dashed #ccc;
  border-radius: 0.5rem;
  padding: 0.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 1;
}

.picto-zone:hover {
  background: transparent;
  border-color: rgb(var(--price-color));
}

.picto-image {
  width: auto;
  height: 100%;
  object-fit: contain;
  mix-blend-mode: multiply;
  background: transparent;
  transition: transform 0.2s ease;
  transform-origin: top right;
}

.picto-controls {
  position: absolute;
  top: 3.5rem;
  right: 0;
  display: flex;
  gap: 0.25rem;
  background: white;
  padding: 0.25rem;
  border-radius: 0.25rem;
  border: 1px solid #ccc;
  z-index: 2;
}

.size-control {
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background: white;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: all 0.2s ease;
}

.size-control:hover:not(:disabled) {
  border-color: rgb(var(--price-color));
  background: #f8f8f8;
}

.size-control:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.background-toggle {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background: white;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
}

.background-toggle:hover {
  border-color: rgb(var(--price-color));
  background: #f8f8f8;
}

.background-toggle .icon {
  font-size: 1.2rem;
}

.background-color-toggle {
  position: absolute;
  bottom: 1rem;
  left: 4rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background: white;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
}

.background-color-toggle:hover {
  border-color: rgb(var(--price-color));
  background: #f8f8f8;
}

.color-picker {
  position: absolute;
  bottom: 4rem;
  left: 4rem;
  background: white;
  border: 1px solid #ccc;
  border-radius: 0.5rem;
  padding: 0.5rem;
  z-index: 20;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.color-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.color-option {
  width: 2rem;
  height: 2rem;
  border: 1px solid #ccc;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.color-option:hover {
  transform: scale(1.1);
  border-color: rgb(var(--price-color));
}

.custom-color-input {
  width: 100%;
  height: 2rem;
  border: 1px solid #ccc;
  border-radius: 0.25rem;
  cursor: pointer;
  padding: 0;
}

.toggle-price-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-top: 0.1rem;
  font-size: 1rem;
  z-index: 5;
  color: #666;
  flex-shrink: 0;
}

/* Styles pour la zone de marque */
.brand-container {
  display: flex;
  margin-top: 1rem;
  justify-content: flex-end;
  padding-right: 1rem; /* Ajouter un padding pour éviter que le logo soit coupé */
}

.align-left {
  justify-content: flex-start;
  padding-left: 1rem; /* Ajouter un padding pour éviter que le logo soit coupé */
}

/* Styles spécifiques pour éviter la coupure des logos */
.brand-zone-info {
  width: 6rem; /* Augmenter la largeur pour éviter la coupure */
  height: 3rem; /* Augmenter la hauteur pour éviter la coupure */
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 2px dashed #ccc;
  border-radius: 0.5rem;
  padding: 0.5rem; /* Augmenter le padding */
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  margin-right: 1rem; /* Réduire la marge */
  overflow: visible; /* Permettre le débordement pour éviter la coupure */
}

.brand-zone-info:hover {
  border-color: rgb(var(--price-color));
  background: rgba(183, 0, 24, 0.05);
}

.brand-controls {
  position: absolute;
  top: -2rem;
  right: 0;
  display: flex;
  gap: 0.25rem;
  background: white;
  padding: 0.25rem;
  border-radius: 0.25rem;
  border: 1px solid #ccc;
  z-index: 50;
}

.remove-brand {
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background: white;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: all 0.2s ease;
}

.remove-brand:hover {
  border-color: rgb(var(--price-color));
  background: #f8f8f8;
}

.brand-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  mix-blend-mode: multiply;
  background: transparent;
  max-width: 100%;
  max-height: 100%;
  /* Ajouter une marge pour éviter la coupure */
  margin: 0.25rem;
}

/* Ajustement pour columnTwo */
.columnTwo .brand-zone-info {
  width: 4rem; /* Augmenter de 3rem à 4rem */
  height: 2rem; /* Augmenter de 1.5rem à 2rem */
}

/* Mode aperçu */
.preview-mode {
  border: none !important;
  /* Préserver les backgrounds des éléments enfants */
}

.preview-mode .product-content {
  padding: 0 !important;
}

.preview-mode .product-info {
  cursor: default !important;
}

.preview-mode .image-container {
  border: none !important;
  /* Préserver le fond de couleur en mode aperçu */
  background: v-bind(backgroundColor) !important;
}

.preview-mode .product-image {
  cursor: default !important;
}

.preview-mode .empty-state-preview {
  /* Suppression de la ligne qui rendait le fond transparent */
}

.preview-mode .default-product-svg {
  opacity: 0.5;
}

/* Masquer les contrôles en mode aperçu */
.preview-mode .remove-button,
.preview-mode .background-toggle,
.preview-mode .background-color-toggle,
.preview-mode .color-picker,
.preview-mode .picto-controls,
.preview-mode .toggle-price-button {
  display: none !important;
}

/* Rendre le contenu non éditable en mode aperçu */
.preview-mode [contenteditable="true"] {
  outline: none !important;
  cursor: default !important;
  user-select: none !important;
}

/* Supprimer les bordures des zones internes en mode aperçu */
.preview-mode .picto-zone,
.preview-mode .brand-zone-info {
  border: none !important;
  /* Préserver les backgrounds personnalisés */
  margin-right: 3rem;
  margin-bottom: 1rem;
}

/* Styles pour l'impression */
@media print {
  .product-block {
    break-inside: avoid;
    page-break-inside: avoid;
    border: none !important;
  }
}

/* Media queries pour la responsivité */
@media (max-width: 768px) {
  .product-content {
    flex-direction: column !important;
    gap: 0.5rem;
    padding: 0.5rem;
  }

  .product-content.image-right {
    flex-direction: column !important;
  }

  .image-section {
    flex: 0 0 auto;
    min-width: 100%;
    max-width: 100%;
    min-height: 200px;
    max-height: 300px;
  }

  .image-container {
    min-height: 200px;
    max-height: 300px;
  }

  .product-image {
    object-fit: contain !important;
    max-width: 100% !important;
    max-height: 100% !important;
    width: auto !important;
    /* height: auto !important; */
  }

  .product-title {
    font-size: 1.4rem !important;
  }

  .product-description {
    font-size: 1.1rem !important;
  }

  .price-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .price-actions {
    align-self: flex-end;
  }
}

@media (max-width: 480px) {
  .product-content {
    padding: 0.25rem;
    gap: 0.25rem;
  }

  .image-section {
    min-height: 150px;
    max-height: 200px;
  }

  .image-container {
    min-height: 150px;
    max-height: 200px;
  }

  .product-image {
    object-fit: contain !important;
    max-width: 100% !important;
    max-height: 100% !important;
    width: auto !important;
    /* height: auto !important; */
  }

  .product-title {
    font-size: 1.2rem !important;
    margin-bottom: 0.25rem;
  }

  .product-description {
    font-size: 1rem !important;
    margin-bottom: 0.5rem;
  }

  .picto-zone {
    width: 5rem;
    height: 2.5rem;
    top: 0.25rem;
    right: 0.25rem;
  }

  .brand-zone-info {
    width: 3rem;
    height: 1.5rem;
  }
}

/* Styles spécifiques pour les très petits écrans (comme dans l'email) */
@media (max-width: 500px) {
  .product-content {
    flex-direction: column !important;
    gap: 0.25rem;
    padding: 0.25rem;
  }

  .image-section {
    min-height: 120px;
    max-height: 180px;
    width: 100%;
  }

  .image-container {
    min-height: 120px;
    max-height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .product-image {
    object-fit: contain !important;
    max-width: 100% !important;
    max-height: 100% !important;
    width: auto !important;
    /* height: auto !important; */
    display: block;
  }

  .product-title {
    font-size: 1rem !important;
    line-height: 1.2;
    margin-bottom: 0.25rem;
  }

  .product-description {
    font-size: 0.9rem !important;
    line-height: 1.3;
    margin-bottom: 0.5rem;
  }

  .price-ht {
    font-size: 1rem !important;
  }

  .price-ttc {
    font-size: 0.8rem !important;
  }
}
</style>
